import React, { useMemo, useState } from "react";
import { X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

type ImageAttachmentStatus = "analyzing" | "ready" | "error";

export type WebImageAttachment = {
  id: string;
  previewUrl: string;
  status: ImageAttachmentStatus;
  analysisText?: string;
};

interface ImageAttachmentPreviewWebProps {
  attachments: WebImageAttachment[];
  onRemove: (id: string) => void;
  onUpdateAnalysis?: (id: string, text: string) => void;
  isPreviewOnly?: boolean;
}

export const ImageAttachmentPreviewWeb = ({
  attachments,
  onRemove,
  onUpdateAnalysis,
  isPreviewOnly = false,
}: ImageAttachmentPreviewWebProps) => {
  const [expanded, setExpanded] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draftText, setDraftText] = useState<string>("");

  const latestAttachment = useMemo(
    () => (attachments.length > 0 ? attachments[attachments.length - 1] : null),
    [attachments],
  );

  const activeAttachment = useMemo(
    () => attachments.find((att) => att.id === activeId) || null,
    [attachments, activeId],
  );

  if (!latestAttachment) {
    return null;
  }

  const renderRow = (attachment: WebImageAttachment) => {
    const isAnalyzing = attachment.status === "analyzing";
    const isError = attachment.status === "error";
    const text =
      isAnalyzing
        ? "Analyzing image..."
        : isError
          ? "Failed to analyze image"
          : attachment.analysisText || "Image added";

    return (
      <div
        key={attachment.id}
        className="flex items-center gap-3 rounded-xl bg-white/90 border border-[#E5EAF0] px-2.5 py-2 shadow-sm"
      >
        <button
          type="button"
          className="relative h-10 w-10 overflow-hidden rounded-lg bg-slate-100 flex-shrink-0"
          onClick={() => {
            setActiveId(attachment.id);
            setDraftText(attachment.analysisText || "");
          }}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            key={attachment.id}
            src={attachment.previewUrl}
            alt=""
            className="h-full w-full object-cover"
          />
        </button>
        <button
          type="button"
          className="flex-1 text-left"
          onClick={() => {
            setActiveId(attachment.id);
            setDraftText(attachment.analysisText || "");
          }}
        >
          <p
            className={cn(
              "line-clamp-2 text-xs",
              isAnalyzing && "text-slate-400",
              isError && "text-red-600",
              !isAnalyzing && !isError && "text-slate-700",
            )}
          >
            {text}
          </p>
        </button>
        {!isPreviewOnly && (
          <button
            type="button"
            onClick={() => onRemove(attachment.id)}
            className="ml-1 inline-flex h-6 w-6 items-center justify-center rounded-full text-[10px] text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
    );
  };

  const content = expanded ? (
    <div className="space-y-2">
      {attachments.map(renderRow)}
    </div>
  ) : (
    <div className="space-y-1.5">
      {renderRow(latestAttachment)}
      {attachments.length > 1 && (
        <button
          type="button"
          className="text-[11px] text-slate-500 hover:text-slate-700"
          onClick={() => setExpanded(true)}
        >
          +{attachments.length - 1} more image(s)
        </button>
      )}
    </div>
  );

  return (
    <>
      <div className="mb-2 rounded-2xl bg-[#F9FAFB] p-2.5 border border-[#E5EAF0]">
        {content}
      </div>

      {activeAttachment && (
        <button
          type="button"
          aria-label="Close image preview"
          className="fixed inset-0 z-[12000] bg-black/25 backdrop-blur-[1px]"
          onClick={() => {
            setActiveId(null);
            setDraftText("");
          }}
        />
      )}

      <Dialog open={!!activeAttachment} onOpenChange={(open) => {
        if (!open) {
          setActiveId(null);
          setDraftText("");
        }
      }}>
        <DialogContent className="max-w-lg z-[12010]">
          {activeAttachment && (
            <>
              <DialogHeader>
                <DialogTitle className="text-sm font-semibold text-slate-900">
                  Image analysis
                </DialogTitle>
                <DialogDescription className="text-xs text-slate-500">
                  Review or adjust how this image will be described to the genie.
                </DialogDescription>
              </DialogHeader>
              <div className="mt-2 space-y-3">
                <div className="relative w-full overflow-hidden rounded-xl bg-slate-100">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={activeAttachment.previewUrl}
                    alt=""
                    className="h-56 w-full object-cover"
                  />
                </div>
                {isPreviewOnly && (
                  <p className="text-sm text-slate-500">
                    {activeAttachment.analysisText}
                  </p>
                )}
                {!isPreviewOnly && (
                  <textarea
                    value={draftText}
                    onChange={(event) => setDraftText(event.target.value)}
                    className="mt-1 w-full rounded-xl border border-[#E5EAF0] bg-white px-3 py-2 text-sm text-slate-800 shadow-sm outline-none focus:border-[#4E9CFF] focus:ring-2 focus:ring-[#4E9CFF33]"
                    rows={4}
                    placeholder="Image analysis will appear here once processing is complete."
                    disabled={activeAttachment.status === "analyzing"}
                  />
                )}
                <div
                  className={cn(
                    "w-full pt-1",
                    !isPreviewOnly && onUpdateAnalysis
                      ? "grid grid-cols-2 gap-2"
                      : "flex justify-center",
                  )}
                >
                  <button
                    type="button"
                    className={cn(
                      "min-h-11 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 active:bg-slate-50",
                      !isPreviewOnly && onUpdateAnalysis && "w-full",
                    )}
                    onClick={() => {
                      setActiveId(null);
                      setDraftText("");
                    }}
                  >
                    Close
                  </button>
                  {!isPreviewOnly && onUpdateAnalysis && (
                    <button
                      type="button"
                      className="w-full min-h-11 rounded-xl bg-[#4E9CFF] px-4 py-2.5 text-sm font-semibold text-white active:bg-[#3D8BE8]"
                      onClick={() => {
                        onUpdateAnalysis(activeAttachment.id, draftText.trim());
                        setActiveId(null);
                        setDraftText("");
                      }}
                      disabled={activeAttachment.status === "analyzing"}
                    >
                      Save
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

